import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { Select } from '@components/Select';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import BubblePreview from '@features/WhatsAppTemplateBuilder/BubblePreview';
import { getPlaceholderByToken } from '@constants/placeholders';
import {
    TEMPLATE_CHANNEL_FILTER,
    WA_LINK_TYPE,
    WA_MAPPING_SOURCE,
    WA_TEMPLATE_STATUS,
} from '@constants/campaignTemplates';

const PreviewBox = styled('div')({
    background: '#E5DDD5',
    borderRadius: 8,
    padding: '16px 12px',
    display: 'flex',
    justifyContent: 'center',
});

const CONTACT_TOKENS = ['name', 'last_name'];

const WhatsAppTemplateSection = ({ isView, requiresLink, isCouponCampaign }) => {
    const requiredLinkType = isCouponCampaign ? WA_LINK_TYPE.COUPON : WA_LINK_TYPE.SURVEY;
    const { t } = useTranslation();
    const { control, setValue, watch } = useFormContext();
    const { data: templates } = useSelector(state => state.campaignTemplates);

    const selectedChannelId = watch('channelId');
    const selectedTemplateId = watch('whatsAppTemplateRegistryId');

    const selectedTemplate = useMemo(
        () => (templates || []).find(template => template.id === selectedTemplateId) || null,
        [templates, selectedTemplateId],
    );

    const templateOptions = useMemo(
        () =>
            (templates || [])
                .filter(
                    template =>
                        template.id === selectedTemplateId ||
                        (template.channel === TEMPLATE_CHANNEL_FILTER.WHATSAPP &&
                            template.status === WA_TEMPLATE_STATUS.APPROVED &&
                            !template.isArchived &&
                            template.isFullyMapped &&
                            !template.isDisconnected &&
                            (template.channels || []).includes(selectedChannelId)),
                )
                .filter(template => template.channel === TEMPLATE_CHANNEL_FILTER.WHATSAPP)
                .map(template => {
                    const linkMismatch = requiresLink !== !!template.isLinkCapable;
                    const linkTypeMismatch =
                        requiresLink &&
                        template.isLinkCapable &&
                        !!template.linkType &&
                        template.linkType !== requiredLinkType;
                    return {
                        value: template.id,
                        label: `${template.name} (${template.language})`,
                        disabled: linkMismatch || linkTypeMismatch,
                        tooltip:
                            requiresLink && !template.isLinkCapable
                                ? t('TemplateLibrary.noLinkButton')
                                : linkMismatch
                                  ? t('TemplateLibrary.linkButtonNotSupported')
                                  : linkTypeMismatch
                                    ? t(
                                          template.linkType === WA_LINK_TYPE.COUPON
                                              ? 'TemplateLibrary.waLinkTypeCouponOnly'
                                              : 'TemplateLibrary.waLinkTypeSurveyOnly',
                                      )
                                    : '',
                    };
                }),
        [templates, selectedTemplateId, selectedChannelId, requiresLink, requiredLinkType, t],
    );

    const fieldMappings = (selectedTemplate?.variableMappings || []).filter(
        mapping => mapping.source === WA_MAPPING_SOURCE.FIELD,
    );
    const hasLinkSlot = (selectedTemplate?.variableMappings || []).some(
        mapping => mapping.source === WA_MAPPING_SOURCE.LINK_SLOT,
    );

    const fieldRowLabel = mapping => {
        const placeholder = getPlaceholderByToken(mapping.token);
        const label = placeholder ? t(placeholder.labelKey) : mapping.token;
        const origin = CONTACT_TOKENS.includes(mapping.token)
            ? t('TemplateLibrary.waFromContact')
            : t('TemplateLibrary.waFromLocation');
        return `${label} — ${origin}`;
    };

    return (
        <>
            <Controller
                control={control}
                name="whatsAppTemplateRegistryId"
                render={({ field, fieldState: { error }, ref }) => (
                    <Select
                        {...field}
                        ref={ref}
                        onChange={({ target: { value } }) => {
                            setValue('whatsAppTemplateRegistryId', value, {
                                shouldDirty: true,
                                shouldValidate: true,
                            });
                        }}
                        options={templateOptions}
                        required
                        disabled={isView}
                        label={t('common.template')}
                        fullWidth
                        style={{ gridColumn: '1 / span 2' }}
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                    />
                )}
            />
            {selectedTemplate && (
                <div style={{ gridColumn: '1 / span 2' }}>
                    <PreviewBox>
                        <BubblePreview
                            components={selectedTemplate.components}
                            headerMediaUrl={selectedTemplate.headerMediaUrl}
                            mappings={selectedTemplate.variableMappings}
                        />
                    </PreviewBox>
                    {(fieldMappings.length > 0 || hasLinkSlot) && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 12 }}>
                            <Typography variant="subtitle2">{t('TemplateLibrary.waVariables')}</Typography>
                            {fieldMappings.map(mapping => (
                                <Typography
                                    key={mapping.param}
                                    variant="caption"
                                    sx={{ color: 'var(--text-secondary)' }}
                                >
                                    {`{{${mapping.param}}}`} · {fieldRowLabel(mapping)}
                                </Typography>
                            ))}
                            {hasLinkSlot && (
                                <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                                    {t('TemplateLibrary.waLinkSlotInfo')}
                                </Typography>
                            )}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default WhatsAppTemplateSection;
